import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

const POLL_INTERVAL_MS = 45000;

const COLOR_CLASSES = {
  green: "text-green-600",
  yellow: "text-yellow-600",
  red: "text-red-600",
  gray: "text-gray-400",
};
const COLOR_BORDER = {
  green: "#16A34A",
  yellow: "#CA8A04",
  red: "#DC2626",
  gray: "#9CA3AF",
};
const STATE_BADGE = {
  open: { label: "Open — Unassigned", className: "bg-gray-100 text-gray-500" },
  unscheduled_time: { label: "No Time Set", className: "bg-gray-100 text-gray-500" },
  upcoming: { label: "Upcoming", className: "bg-gray-100 text-gray-500" },
  late: { label: "⚠ Late", className: "bg-yellow-100 text-yellow-700" },
  missed: { label: "✕ Missed", className: "bg-red-100 text-red-700" },
  in_progress: { label: "● In Progress", className: "bg-green-100 text-green-700" },
  completed: { label: "✓ Completed", className: "bg-green-100 text-green-700" },
};
const URGENCY_BADGE = {
  red: "bg-red-100 text-red-700",
  yellow: "bg-yellow-100 text-yellow-700",
  gray: "bg-gray-100 text-gray-500",
};
function daysOutLabel(daysOut) {
  if (daysOut === 0) return "Today";
  if (daysOut === 1) return "Tomorrow";
  return `In ${daysOut} days`;
}

function StatCard({ label, value, tone, to }) {
  const content = (
    <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 h-full" style={{ borderColor: tone || "#154E93" }}>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: "#154E93" }}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function ScoreCard({ label, pct, color, sublabel }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 h-full" style={{ borderColor: COLOR_BORDER[color] }}>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${COLOR_CLASSES[color]}`}>{pct}%</div>
      {sublabel && <div className="text-xs text-gray-400 mt-0.5">{sublabel}</div>}
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollRef = useRef(null);

  function load() {
    api.executive
      .overview()
      .then((d) => {
        setData(d);
        setLastUpdated(new Date());
        setError("");
      })
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!data) return <div className="text-gray-500">Loading…</div>;

  return (
    <div>
      <div
        className="rounded-lg p-6 mb-6 flex items-center justify-between overflow-hidden relative"
        style={{ backgroundColor: "#154E93" }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-1.5" style={{ backgroundColor: "#F3850C" }} />
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="JyBek Home Care Services" className="h-14 w-auto bg-white rounded p-1.5" />
          <div>
            <div className="text-white text-xl font-bold">JyBek Home Care Services</div>
            <div className="text-sm" style={{ color: "#F3850C" }}>Where care meets comfort</div>
          </div>
        </div>
        <div className="hidden md:block text-right text-white/70 text-sm">
          Clients · Caregivers · Scheduling · Accounting · Training · HR
        </div>
      </div>

      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Executive Dashboard</h1>
        {lastUpdated && (
          <span className="text-xs text-gray-400">
            Updated {lastUpdated.toLocaleTimeString()} · refreshes every {POLL_INTERVAL_MS / 1000}s
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">Agency health at a glance</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="Active Clients" value={data.counts.active_clients} to="/admin/hr/clients" />
        <StatCard label="Active Field Duty Staff" value={data.counts.active_field_duty_staff} to="/admin/employees" />
        <StatCard label="Active Office Staff" value={data.counts.active_office_staff} to="/admin/employees" />
        <StatCard
          label={`Unassigned Visits (${data.unassigned_visits.window_days}d)`}
          value={data.unassigned_visits.count}
          tone={data.unassigned_visits.count > 0 ? "#DC2626" : "#16A34A"}
          to="/admin/scheduling"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <ScoreCard
          label="Compliance Score"
          pct={data.compliance.score}
          color={data.compliance.color}
          sublabel={`${data.compliance.compliant} compliant · ${data.compliance.expiring_soon} expiring · ${data.compliance.non_compliant} non-compliant`}
        />
        <ScoreCard
          label="Shift Fill Rate (Today)"
          pct={data.shift_fill_rate.rate}
          color={data.shift_fill_rate.color}
          sublabel={`${data.shift_fill_rate.filled} of ${data.shift_fill_rate.total} shifts filled`}
        />
        <Link to="/admin/dashboard">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 h-full" style={{ borderColor: "#154E93" }}>
            <div className="text-xs text-gray-500 font-medium">Training Compliance</div>
            <div className="text-sm font-semibold mt-1" style={{ color: "#154E93" }}>View full matrix →</div>
            <div className="text-xs text-gray-400 mt-0.5">Per-caregiver course completion & overdue training</div>
          </div>
        </Link>
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 h-full opacity-60" style={{ borderColor: "#9CA3AF" }}>
          <div className="text-xs text-gray-500 font-medium">Revenue & Profit</div>
          <div className="text-sm font-semibold mt-1 text-gray-400">Coming soon</div>
          <div className="text-xs text-gray-400 mt-0.5">{data.financials.message}</div>
        </div>
      </div>

      {data.celebrations && data.celebrations.items.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold" style={{ color: "#154E93" }}>
              🎉 Celebrations — Next {data.celebrations.window_days} Days
            </h2>
            {data.celebrations.today_count > 0 && (
              <span className="text-xs font-semibold" style={{ color: "#F3850C" }}>
                {data.celebrations.today_count} today!
              </span>
            )}
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: "#154E93" }}>
                <tr className="text-white text-left">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Occasion</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.celebrations.items.map((c, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {c.type === "birthday" ? "🎂 Birthday" : "🏆 Work Anniversary"}
                    </td>
                    <td className="px-4 py-2 text-gray-500 capitalize">{c.subject_type}</td>
                    <td className="px-4 py-2 text-gray-500">{(c.date || "").slice(5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold" style={{ color: "#154E93" }}>
          Unassigned Visits — Next {data.unassigned_visits.window_days} Days
        </h2>
        <span className="text-xs text-gray-400">Find coverage before these become missed visits</span>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: "#154E93" }}>
            <tr className="text-white text-left">
              <th className="px-4 py-2">Client</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {data.unassigned_visits.items.map((v) => (
              <tr key={v.id} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{v.client_name}</td>
                <td className="px-4 py-2 text-gray-500">{v.shift_date}</td>
                <td className="px-4 py-2 text-gray-500">
                  {v.start_time || "—"}{v.end_time ? ` – ${v.end_time}` : ""}
                </td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${URGENCY_BADGE[v.urgency]}`}>
                    {daysOutLabel(v.days_out)}
                  </span>
                </td>
              </tr>
            ))}
            {data.unassigned_visits.items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No unassigned visits in this window. Nice.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold" style={{ color: "#154E93" }}>Live Shift Monitoring — Today</h2>
        <span className="text-xs text-gray-400">Late: no clock-in past scheduled start · Missed: 30+ min past</span>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: "#154E93" }}>
            <tr className="text-white text-left">
              <th className="px-4 py-2">Client</th>
              <th className="px-4 py-2">Caregiver</th>
              <th className="px-4 py-2">Scheduled</th>
              <th className="px-4 py-2">Clock In</th>
              <th className="px-4 py-2">Clock Out</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.live_shifts.map((s) => {
              const badge = STATE_BADGE[s.state] || STATE_BADGE.upcoming;
              return (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{s.client_name}</td>
                  <td className="px-4 py-2">{s.employee_name || <span className="text-gray-400">Unassigned</span>}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {s.start_time || "—"}{s.end_time ? ` – ${s.end_time}` : ""}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{s.clock_in_actual || "—"}</td>
                  <td className="px-4 py-2 text-gray-500">{s.clock_out_actual || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>{badge.label}</span>
                  </td>
                </tr>
              );
            })}
            {data.live_shifts.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No shifts scheduled today.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
